import {HttpClient, HttpHeaders, HttpParams} from '@angular/common/http';
import {DestroyRef, inject, Injectable} from '@angular/core';
import {Timezone} from '@syncfusion/ej2-angular-schedule';
import {Observable, Subject, tap} from 'rxjs';
import {LocalStorageService} from '../local-storage.service';
import {MainDataModels} from '../models/maindata-models';
import {SwissAppConfigModels} from '../models/swissappconfig-models';
import {DatumFormatPipe} from '../pipes/datum-format.pipe';
import {HelperService} from './helper.service';
import {UserAuthenticationService} from './user-authentication.service';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {CalendarService} from './calendar.service';

@Injectable({
    providedIn: 'root',
})
export class MainDataService {
    private storageWebMain: string = 'SwissWebMainDB';
    private currTermin?: MainDataModels.Termin;
    private webServiceConfig?: SwissAppConfigModels.WebServiceConfig;
    private dbTerminAdressen?: MainDataModels.TerminAdresse[];
    private dbMitarbeiter?: MainDataModels.Mitarbeiter[];
    private dbMitarbeiterGruppen?: MainDataModels.MitarbeiterGruppe[];
    private dbMitarbeiterGruppenZuord?: MainDataModels.MitarbeiterGruppeZuord[];
    private dbKategorien?: MainDataModels.Kategorie[];
    private reloadTerminFunc?: () => void;
    private selectedAgendaMitarbeiter: string[] = [];
    private timezone: Timezone;
    private noteCount: Subject<{ noteCount: number, unreadNotes: number }> = new Subject<{ noteCount: number, unreadNotes: number }>();
    private destroyRef = inject(DestroyRef);

    constructor(private http: HttpClient, private userAuthService: UserAuthenticationService, private helperService: HelperService, private localStorageService: LocalStorageService, private _datumFormatPipe: DatumFormatPipe,) {
        this.helperService.getSwissWebServiceConfig$().pipe(
            takeUntilDestroyed(),
            tap((config) => {
                this.webServiceConfig = config;
            })
        ).subscribe();
        // Terminserien
        this.timezone = new Timezone();
    }

    /**
     * @deprecated {@link CalendarService} mutations (saveAppointment/moveAppointment/deleteAppointment) refresh the calendar automatically.
     */
    registerTerminFunc(fn: () => void) {
        this.reloadTerminFunc = fn;
    }

    getMitarbeiter(): Observable<MainDataModels.Mitarbeiter[]> {
        let url = this.webServiceConfig!.userUrl;
        return this.http.get<MainDataModels.Mitarbeiter[]>(url + '/Mitarbeiter', this.helperService.getSwissHttpOptions(this.userAuthService.currentUserInfo));
    }

    // Mitarbeiter live holen, geht das nicht, dann von IndexedDB
    /**
     * @deprecated Use {@link CalendarService.getEmployees} instead.
     */
    getMitarbeiterWithDB(): Promise<MainDataModels.Mitarbeiter[]> {
        return new Promise((resolve) => {
            this.getMitarbeiter().subscribe({
                next: (res) => {
                    this.localStorageService.add('mitarbeiter', res, this.storageWebMain);
                    this.dbMitarbeiter = res;
                    resolve(this.dbMitarbeiter);
                }, error: () => {
                    this.localStorageService
                        .get('mitarbeiter', this.storageWebMain)
                        .then((value) => {
                            if (value === false) {
                                resolve([]);
                            } else {
                                this.dbMitarbeiter = <MainDataModels.Mitarbeiter[]> value;
                                resolve(this.dbMitarbeiter);
                            }
                        });
                },
            });
        });
    }

    getMitarbeiterGruppen(): Observable<MainDataModels.MitarbeiterGruppe[]> {
        let url = this.webServiceConfig!.userUrl;
        return this.http.get<MainDataModels.MitarbeiterGruppe[]>(url + '/MitarbeiterGruppen', this.helperService.getSwissHttpOptions(this.userAuthService.currentUserInfo));
    }

    // MitarbeiterGruppen live holen, geht das nicht, dann von IndexedDB
    getMitarbeiterGruppenWithDB(): Promise<MainDataModels.MitarbeiterGruppe[]> {
        return new Promise((resolve) => {
            this.getMitarbeiterGruppen().subscribe({
                next: (res) => {
                    this.localStorageService.add('mitarbeitergruppen', res, this.storageWebMain);
                    this.dbMitarbeiterGruppen = res;
                    resolve(this.dbMitarbeiterGruppen);
                }, error: () => {
                    this.localStorageService
                        .get('mitarbeitergruppen', this.storageWebMain)
                        .then((value) => {
                            if (value === false) {
                                resolve([]);
                            } else {
                                this.dbMitarbeiterGruppen = <MainDataModels.MitarbeiterGruppe[]> value;
                                resolve(this.dbMitarbeiterGruppen);
                            }
                        });
                },
            });
        });
    }

    getMitarbeiterGruppenZuord(): Observable<MainDataModels.MitarbeiterGruppeZuord[]> {
        let url = this.webServiceConfig!.userUrl;
        return this.http.get<MainDataModels.MitarbeiterGruppeZuord[]>(url + '/MitarbeiterGruppenZuord', this.helperService.getSwissHttpOptions(this.userAuthService.currentUserInfo));
    }

    // MitarbeiterGruppenZuord live holen, geht das nicht, dann von IndexedDB
    getMitarbeiterGruppenZuordWithDB(): Promise<MainDataModels.MitarbeiterGruppeZuord[]> {
        return new Promise((resolve) => {
            this.getMitarbeiterGruppenZuord().subscribe({
                next: (res) => {
                    this.localStorageService.add('mitarbeitergruppenzuord', res, this.storageWebMain);
                    this.dbMitarbeiterGruppenZuord = res;
                    resolve(this.dbMitarbeiterGruppenZuord);
                }, error: () => {
                    this.localStorageService
                        .get('mitarbeitergruppenzuord', this.storageWebMain)
                        .then((value) => {
                            if (value === false) {
                                resolve([]);
                            } else {
                                this.dbMitarbeiterGruppenZuord = <MainDataModels.MitarbeiterGruppeZuord[]> value;
                                resolve(this.dbMitarbeiterGruppenZuord);
                            }
                        });
                },
            });
        });
    }

    getAllTermine(): Observable<MainDataModels.Termin[]> {
        let url = this.webServiceConfig!.termUrl;
        return this.http.get<MainDataModels.Termin[]>(url + '/TerminData', this.helperService.getSwissHttpOptions(this.userAuthService.currentUserInfo));
    }


    /**
     * @deprecated Use {@link CalendarService.getCalendarData} instead.
     */
    getEmployeesKalender(): Observable<MainDataModels.KalenderDaten> {
        const headers = new HttpHeaders({
            Authorization: this.helperService.getSwissAuthorization(this.userAuthService.currentUserInfo),
        });

        const params: HttpParams = new HttpParams().set('mitarbeiter', this.selectedAgendaMitarbeiter.join(', '));

        const options = {
            headers: headers, params: params,
        };

        let url = this.webServiceConfig!.termUrl;
        return this.http.get<MainDataModels.KalenderDaten>(url + '/MitarbeiterKalenderData', options);
    }

    getUserTermine(): Observable<MainDataModels.Termin[]> {
        let url = this.webServiceConfig!.termUrl;
        const user = this.userAuthService.getCurrentAuthenticatedUser()!;

        return this.http.get<MainDataModels.Termin[]>(url + '/UserTerminData?loginname=' + user.loginname, this.helperService.getSwissHttpOptions(this.userAuthService.currentUserInfo));
    }

    getUserTermineHeute(): Observable<MainDataModels.Termin[]> {
        let url = this.webServiceConfig!.termUrl;
        const user = this.userAuthService.getCurrentAuthenticatedUser()!;

        return this.http.get<MainDataModels.Termin[]>(url + '/UserTerminDataToday?loginname=' + user.loginname, this.helperService.getSwissHttpOptions(this.userAuthService.currentUserInfo));
    }

    /**
     * @deprecated Use {@link CalendarService.getTerminById} instead.
     */
    getTerminByID(CalendarID: string): Observable<MainDataModels.Termin> {
        let url = this.webServiceConfig!.termUrl;
        return this.http.get<MainDataModels.Termin>(url + '/TerminByID?CalendarID=' + CalendarID, this.helperService.getSwissHttpOptions(this.userAuthService.currentUserInfo));
    }

    getTerminAdressen(): Observable<MainDataModels.TerminAdresse[]> {
        let url = this.webServiceConfig!.adrUrl;
        return this.http.get<MainDataModels.TerminAdresse[]>(url + '/TerminAdressen', this.helperService.getSwissHttpOptions(this.userAuthService.currentUserInfo));
    }

    getTerminObjektAdressen(objectID: string): Observable<MainDataModels.ObjektAdresseSlim[]> {
        let url = this.webServiceConfig!.termUrl;
        return this.http.get<MainDataModels.ObjektAdresseSlim[]>(url + '/ObjektAdressen?ObjectID=' + objectID, this.helperService.getSwissHttpOptions(this.userAuthService.currentUserInfo));
    }

    getKategorien() {
        let url = this.webServiceConfig!.termUrl;
        return this.http.get<MainDataModels.Kategorie[]>(url + '/TerminKategorien?mandant=' + this.userAuthService.getCurrentAuthenticatedUser()?.mandatorID, this.helperService.getSwissHttpOptions(this.userAuthService.currentUserInfo));
    }

    getKategorienWithDB(): Promise<MainDataModels.Kategorie[]> {
        return new Promise((resolve) => {
            this.getKategorien().subscribe({
                next: (res) => {
                    this.localStorageService.add('terminkategorien', res, this.storageWebMain);
                    this.dbKategorien = res;
                    resolve(this.dbKategorien);
                }, error: () => {
                    this.localStorageService
                        .get('terminkategorien', this.storageWebMain)
                        .then((value) => {
                            if (value === false) {
                                resolve([]);
                            } else {
                                this.dbKategorien = <MainDataModels.Kategorie[]> value;
                                resolve(this.dbKategorien);
                            }
                        });
                },
            });
        });
    }

    // Ausgewählte Mitarbeiter aus den Terminen laden
    /**
     * @deprecated Use {@link CalendarService.selectedEmployees$} instead.
     */
    loadSelectedAgendaMitarbeiter(): Promise<string[]> {
        return new Promise((resolve) => {
            const user = this.userAuthService.getCurrentAuthenticatedUser()!;
            this.localStorageService.get('last_selected_schedule_employees', this.storageWebMain).then((value) => {
                if (value === false) {
                    // Es konnte keinen Wert in der indexeddb für ausgewählten Mitarbeiter ermittelt werden
                    // Aktuell angemeldeten Benutzer auswählen
                    this.selectedAgendaMitarbeiter = [];
                    let currEmpID = user.employeeID;
                    let currMitarbeiter = this.dbMitarbeiter?.filter((mi) => mi.fK_EmployeeID === currEmpID)[0];
                    if (currMitarbeiter && currMitarbeiter.userID) {
                        this.selectedAgendaMitarbeiter.push(currMitarbeiter.loginname);
                    }
                    resolve(this.selectedAgendaMitarbeiter);
                } else {
                    if (value) {
                        this.selectedAgendaMitarbeiter = <string[]> value;
                        resolve(this.selectedAgendaMitarbeiter);
                    }
                }
            });
        });
    }

    // Ausgewählten Termin aus Agenda in IndexedDB speichern
    // (Beim nächsten Aufruf der Agenda ist dieser gleich aktiv)
    /**
     * @deprecated Use {@link CalendarService.rememberLastAppointment} instead.
     */
    async saveChoosedAppointmentToDB(id: string): Promise<void> {
        await this.localStorageService.add('last_used_appointment', id, this.storageWebMain);
    }

    /**
     * @deprecated The calendar now emits the selected appointment via its
     * `appointmentSelected` output instead of relying on shared state.
     */
    setTermin(termin: MainDataModels.Termin): void {
        this.currTermin = termin;
    }

    getTerminKundenAdresseZeile(adressID: string): Promise<string> {
        return new Promise((resolve) => {
            this.getTerminAdresse(adressID).then((value) => {
                let kunde = '';
                if (value != undefined) {
                    let terminAdr: MainDataModels.TerminAdresse = value as unknown as MainDataModels.TerminAdresse;
                    if (terminAdr.firstName) {
                        kunde = terminAdr.firstName + ' ';
                    }

                    kunde += terminAdr.lastName;
                    if (terminAdr.street1) {
                        kunde += ', ' + terminAdr.street1;
                    }

                    if (terminAdr.zip && terminAdr.city) {
                        kunde += ', ' + terminAdr.zip + ' ' + terminAdr.city;
                    }

                    resolve(kunde);
                } else {
                    resolve('');
                }
            });
        });
    }

    async getTerminAdresse(adressID: string): Promise<MainDataModels.TerminAdresse | undefined> {
        return await new Promise((resolve) => {
            this.getTerminAdressen().subscribe({
                next: (res) => {
                    // Wenn TerminAdressen empfangen wurden, diese in die IndexedDB speichern
                    this.dbTerminAdressen = res;
                    this.localStorageService.add('terminAdressen', res, this.storageWebMain);
                    resolve(this.getTerminAdresseFromDB(adressID));
                }, error: () => {
                    resolve(this.getTerminAdresseFromDB(adressID));
                },
            });
        });
        //return this.getTerminAdresseFromDB(adressID);
    }

    async getTerminAdresseFromDB(adressID: string): Promise<MainDataModels.TerminAdresse | undefined> {
        //let terminAdresse: MainDataModels.TerminAdresse = MainDataModels.EmptyTerminAdresse();
        return await new Promise((resolve) => {
            this.localStorageService
                .get('terminAdressen', this.storageWebMain)
                .then((value) => {
                    if (value === false) {
                        // Es konnte keinen Wert in der indexeddb für Stammdaten ermittelt werden
                        resolve(undefined);
                    } else {
                        if (value) {
                            this.dbTerminAdressen = <MainDataModels.TerminAdresse[]> value;
                            let ta = this.dbTerminAdressen.filter((x) => x.adressID == adressID);
                            if (ta && ta.length === 1) {
                                resolve(ta[0]);
                            } else {
                                resolve(undefined);
                            }
                        } else {
                            resolve(undefined);
                        }
                    }
                });
        });
    }

    getMitarbeiterGruppenExists(): Promise<boolean> {
        return new Promise((resolve) => {
            this.localStorageService
                .get('mitarbeitergruppenzuord', this.storageWebMain)
                .then((value) => {
                    if (value === false) {
                        // Es konnte keinen Wert in der indexeddb für Stammdaten ermittelt werden
                        resolve(false);
                    } else {
                        if (value) {
                            this.dbMitarbeiterGruppenZuord = <MainDataModels.MitarbeiterGruppeZuord[]> value;
                            resolve(this.dbMitarbeiterGruppenZuord && this.dbMitarbeiterGruppenZuord.length > 0);
                        }
                        resolve(false);
                    }
                });
        });
    }

    /**
     * @deprecated Use {@link CalendarService.saveTermin} instead.
     */
    saveTermin(terminMitBenutzer: MainDataModels.TerminMitBenutzer) {
        const user = this.userAuthService.getCurrentAuthenticatedUser()!;

        const storeInOL = (terminMitBenutzer.termin as any)?.storeInOL;
        terminMitBenutzer.termin.storeInOL = storeInOL === 1 || storeInOL === true || storeInOL === '1' ? 1 : 0;

        if (terminMitBenutzer.istNeuerTermin) {
            terminMitBenutzer.termin.createdBy = user.loginname;
        } else {
            terminMitBenutzer.termin.modifiedBy = user.loginname;
        }
        let url = this.webServiceConfig!.termUrl;
        return this.http.post<MainDataModels.TerminMitBenutzer>(url + '/UploadTermin', terminMitBenutzer, this.helperService.getSwissHttpOptions(this.userAuthService.currentUserInfo));
    }

    /**
     * @deprecated Use {@link CalendarService.moveTermin} instead.
     */
    moveTermin(terminVerschiebung: MainDataModels.TerminVerschiebung): Promise<boolean> {
        return new Promise((resolve) => {
            let url = this.webServiceConfig!.termUrl;
            this.http
                .post<MainDataModels.TerminVerschiebung>(url + '/MoveTermin', terminVerschiebung, this.helperService.getSwissHttpOptions(this.userAuthService.currentUserInfo))
                .subscribe({
                    next: () => {
                        resolve(true);
                    }, error: () => (resolve(false)),
                });
        });
    }

    loadTermin(): Observable<MainDataModels.TerminMitBenutzer> {
        let url = this.webServiceConfig!.termUrl;
        return this.http.get<MainDataModels.TerminMitBenutzer>(url + '/GetTermin?calendarID=' + this.currTermin!.id, this.helperService.getSwissHttpOptions(this.userAuthService.currentUserInfo));
    }

    /**
     * @deprecated Use {@link CalendarService.loadTerminWithUsers} instead.
     */
    loadTerminByID(calendarID: string): Observable<MainDataModels.TerminMitBenutzer> {
        let url = this.webServiceConfig!.termUrl;
        return this.http.get<MainDataModels.TerminMitBenutzer>(url + '/GetTermin?calendarID=' + calendarID, this.helperService.getSwissHttpOptions(this.userAuthService.currentUserInfo));
    }

    delTermin(id: string) {
        const formData = new FormData();
        const headers = new HttpHeaders({
            Authorization: this.helperService.getSwissAuthorization(this.userAuthService.currentUserInfo),
        });
        const params: HttpParams = new HttpParams().set('id', id);

        const options = {
            headers: headers, params: params,
        };
        let url = this.webServiceConfig!.termUrl;
        return this.http.post<Response>(url + '/DeleteTermin', formData, options);
    }

    saveAufgabe(aufgabeMitBenutzer: MainDataModels.AufgabeMitBenutzer) {
        const user = this.userAuthService.getCurrentAuthenticatedUser()!;

        if (aufgabeMitBenutzer.istNeueAufgabe) {
            aufgabeMitBenutzer.aufgabe.createdBy = user.loginname;
        } else {
            aufgabeMitBenutzer.aufgabe.modifiedBy = user.loginname;
        }

        let url = this.webServiceConfig!.aufgUrl;
        return this.http.post<MainDataModels.AufgabeMitBenutzer>(url + '/UploadAufgabe', aufgabeMitBenutzer, this.helperService.getSwissHttpOptions(this.userAuthService.currentUserInfo));
    }

    getUserTasks(): Observable<MainDataModels.Aufgabe[]> {
        const user = this.userAuthService.getCurrentAuthenticatedUser()!;

        let url = this.webServiceConfig!.aufgUrl;
        return this.http.get<MainDataModels.Aufgabe[]>(url + '/UserAufgabenData?loginname=' + user.loginname, this.helperService.getSwissHttpOptions(this.userAuthService.currentUserInfo));
    }

    getOpenUserTasks(): Observable<MainDataModels.Aufgabe[]> {
        let url = this.webServiceConfig!.aufgUrl;
        const user = this.userAuthService.getCurrentAuthenticatedUser()!;

        return this.http.get<MainDataModels.Aufgabe[]>(url + '/UserAufgabenOffen?loginname=' + user.loginname, this.helperService.getSwissHttpOptions(this.userAuthService.currentUserInfo));
    }

    loadTaskByID(taskID: string): Observable<MainDataModels.AufgabeMitBenutzer> {
        let url = this.webServiceConfig!.aufgUrl;
        return this.http.get<MainDataModels.AufgabeMitBenutzer>(url + '/GetAufgabe?taskID=' + taskID, this.helperService.getSwissHttpOptions(this.userAuthService.currentUserInfo));
    }

    saveNotiz(notizMitBenutzer: MainDataModels.NotizMitBenutzer) {
        const user = this.userAuthService.getCurrentAuthenticatedUser()!;

        if (notizMitBenutzer.istNeueNotiz) {
            notizMitBenutzer.notiz.createdBy = user.loginname;
        } else {
            notizMitBenutzer.notiz.modifiedBy = user.loginname;
        }
        let url = this.webServiceConfig!.noteUrl;
        return this.http.post<MainDataModels.NotizMitBenutzer>(url + '/UploadNotiz', notizMitBenutzer, this.helperService.getSwissHttpOptions(this.userAuthService.currentUserInfo));
    }

    getUserNotes(): Observable<MainDataModels.Notiz[]> {
        const user = this.userAuthService.getCurrentAuthenticatedUser()!;
        let url = this.webServiceConfig!.noteUrl;
        return this.http.get<MainDataModels.Notiz[]>(url + '/UserNotizenData?loginname=' + user.loginname, this.helperService.getSwissHttpOptions(this.userAuthService.currentUserInfo));
    }

    loadNoteByID(noteID: string): Observable<MainDataModels.NotizMitBenutzer> {
        let url = this.webServiceConfig!.noteUrl;
        return this.http.get<MainDataModels.NotizMitBenutzer>(url + '/GetNotiz?noteID=' + noteID, this.helperService.getSwissHttpOptions(this.userAuthService.currentUserInfo));
    }

    markNoteAsRead(noteID: string): Observable<boolean> {
        let url = this.webServiceConfig!.noteUrl;
        return this.http.get<boolean>(url + '/MarkNoteAsRead?noteID=' + noteID, this.helperService.getSwissHttpOptions(this.userAuthService.currentUserInfo));
    }

    setNoteCount(notes: MainDataModels.Notiz[]): void {
        const unreadNotes = notes.filter(note => !note.wasRead);
        this.noteCount.next({noteCount: notes.length, unreadNotes: unreadNotes.length});
    }

    getNoteCount(): Subject<{ noteCount: number, unreadNotes: number }> {
        return this.noteCount;
    }

    /**
     * @deprecated {@link CalendarService} mutations (saveAppointment/moveAppointment/deleteAppointment) refresh the calendar automatically.
     */
    reloadCalendarData() {
        if (this.reloadTerminFunc) {
            this.reloadTerminFunc();
        }
    }

    getSuppliers(): Observable<MainDataModels.Lieferant[]> {
        let url = this.webServiceConfig!.weUrl;
        return this.http.get<MainDataModels.Lieferant[]>(url + '/GetSuppliers?MandantID=100', this.helperService.getSwissHttpOptions(this.userAuthService.currentUserInfo));
    }

    getOrdersFromSupplier(supplierID: number): Observable<MainDataModels.Bestellung[]> {
        let url = this.webServiceConfig!.weUrl;
        return this.http.get<MainDataModels.Bestellung[]>(url + '/GetOrders?MandantID=100' + '&SupplierID=' + supplierID, this.helperService.getSwissHttpOptions(this.userAuthService.currentUserInfo));
    }

    getOrderPositionFromOrder(orderHeaderID: string): Observable<MainDataModels.BestellPosition[]> {
        let url = this.webServiceConfig!.weUrl;
        return this.http.get<MainDataModels.BestellPosition[]>(url + '/GetOrderDetails' + '?OrderHeaderID=' + orderHeaderID, this.helperService.getSwissHttpOptions(this.userAuthService.currentUserInfo));
    }

    setOrderPosition(OrderID: string, amount: number, delivered: number, deliverydate: string,) {
        const formData = new FormData();
        const headers = new HttpHeaders({
            Authorization: this.helperService.getSwissAuthorization(this.userAuthService.currentUserInfo),
        });
        const params: HttpParams = new HttpParams()
            .set('OrderID', OrderID)
            .set('DeliveryQuantity', amount)
            .set('Delivered', delivered)
            .set('Deliverydate', deliverydate);

        const options = {
            headers: headers, params: params,
        };

        let url = this.webServiceConfig!.weUrl;
        return this.http.post(url + '/SetOrderDetailState', formData, options);
    }

    /**
     * @deprecated Use {@link CalendarService.getRecurrenceRule} instead.
     */
    public getRecurrenceRule(pendenz: MainDataModels.Termin, terminSerie: MainDataModels.TerminSerie): string {
        /*
        Beschreibung der Logik im Navi 10:
        "X:\Entwicklung\dokumentation\Programmierung\Navigator\Serientermine in SwissSoft.doc"
        */

        // Welche Wochentage müssen berücksichtigt werden?
        let byDay = '';
        if (terminSerie.dayType && terminSerie.dayType !== 0) {
            byDay = this.getByDay(terminSerie.dayType);
        }

        /*TODO Imagine if there was a data type for exactly this
        recurrenceType:
          0: Täglich
          1: Wöchentlich
          2: Monatlich
          3: Jeden x-ten Monat
          4: ???? => sollte es nicht geben
          5: Jährlich
        */
        let rule = 'FREQ=';
        switch (terminSerie.recurrenceType) {
            case 0: {
                rule += 'DAILY;' + byDay;
                if (terminSerie.ordinalDay && terminSerie.ordinalDay > 0) {
                    rule += 'INTERVAL=' + terminSerie.ordinalDay + ';';
                }
                break;
            }
            case 1: {
                rule += 'WEEKLY;' + byDay;
                if (terminSerie.ordinalWeek && terminSerie.ordinalWeek > 0) {
                    rule += 'INTERVAL=' + terminSerie.ordinalWeek + ';';
                }
                break;
            }
            case 2: {
                rule += 'MONTHLY;' + byDay;
                if (terminSerie.day && terminSerie.day > 0) {
                    rule += 'BYMONTHDAY=' + terminSerie.day + ';';
                }
                if (terminSerie.ordinalMonth && terminSerie.ordinalMonth > 0) {
                    rule += 'INTERVAL=' + terminSerie.ordinalMonth + ';';
                }
                break;
            }
            case 3: {
                rule += 'MONTHLY;' + byDay;
                if (terminSerie.ordinalNumber && terminSerie.ordinalNumber > 0) {
                    if (terminSerie.ordinalNumber === 5) /* Letzter Tag */{
                        rule += 'BYSETPOS=-1;';
                    } else {
                        rule += 'BYSETPOS=' + terminSerie.ordinalNumber + ';';
                    }
                }
                if (terminSerie.ordinalMonth && terminSerie.ordinalMonth > 0) {
                    rule += 'INTERVAL=' + terminSerie.ordinalMonth + ';';
                }
                break;
            }
            case 4: {
                console.error('Diese Termin-Serie ist nicht bekannt: ' + terminSerie.recurrenceType + ' / Termin-Id: ' + pendenz.id);
                break;
            }
            case 5: {
                rule += 'YEARLY;';
                if (terminSerie.day > 0) {
                    rule += 'BYMONTHDAY=' + terminSerie.day + ';';
                }
                if (terminSerie.month > 0) {
                    rule += 'BYMONTH=' + terminSerie.month + ';';
                }
                rule += 'INTERVAL=' + 1 + ';';

                break;
            }
        }

        // Anzahl Termine abhandeln
        if (terminSerie.occurrences && terminSerie.occurrences > 0) {
            rule += 'COUNT=' + terminSerie.occurrences + ';';
        } else {
            // Ende Datum vorhanden?
            if (terminSerie.endDate) {
                let ende = this.timezone.add(new Date(terminSerie.endDate), 'Europe/Zurich');
                if (ende.getFullYear() < 9900) {
                    rule += 'UNTIL=' + this._datumFormatPipe.getSyncfusionCalendarDateStr(ende);
                } else {
                    // NIE aus dem Navi 10 wird als Count = 100 abgehandelt
                    rule += 'COUNT=100;';
                }
            }
        }

        return rule;
    }

    refreshNoteCount() {
        return new Promise((resolve) => {
            this.getUserNotes().pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
                next: (res) => {
                    this.setNoteCount(res);
                    resolve(true);
                }
            });
        });
    }

    //TODO: this should be in backend
    private getByDay(dayType: number): string {
        let result = 'BYDAY=';

        if (dayType === 256) // Alle Tage in der Woche
        {
            result += 'SU,MO,TU,WE,TH,FR,SA;';
        } else {
            if (dayType >= 64) { // Sonntag ist definiert
                result += 'SU,';
                dayType -= 64;
            }
            if (dayType >= 32) { // Samstag ist definiert
                result += 'SA,';
                dayType -= 32;
            }
            if (dayType >= 16) { // Freitag ist definiert
                result += 'FR,';
                dayType -= 16;
            }
            if (dayType >= 8) { // Donnerstag ist definiert
                result += 'TH,';
                dayType -= 8;
            }
            if (dayType >= 4) { // Mittwoch ist definiert
                result += 'WE,';
                dayType -= 4;
            }
            if (dayType >= 2) { // Dienstag ist definiert
                result += 'TU,';
                dayType -= 2;
            }
            if (dayType >= 1) { // Montag ist definiert
                result += 'MO,';
                dayType -= 1;
            }
            // hier muss das letzte Zeichen ein Komma sein, sonst stimmt etwas nicht
            result = result.substring(0, result.length - 1) + ';';
        }
        return result;
    }
}
